import React, { useLayoutEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { Colours } from '../tokens';
const KEY_UP = '38';
const KEY_DOWN = '40';
const STEP = 0.1;
const getStyles = createUseStyles({
    root: {
        position: 'relative',
        border: `12px solid ${Colours.septernary}`,
        borderRadius: 12,
    },
    line: {
        width: '100%',
        border: `1px solid ${Colours.white}`,
        borderRight: 'none',
        borderLeft: 'none',
    },
    point: {
        width: '12px',
        height: '12px',
        backgroundColor: Colours.white,
        border: `1px solid ${Colours.quinternary}`,
        borderRadius: 6,
        position: 'absolute',
        top: '-5px',
        '&:hover': {
            boxShadow: `0px 0px 0px 3px ${Colours.pointBorder}`,
        },
        '&:focus': {
            outline: `3px solid ${Colours.pointBorder}`,
        },
    },
    range: {
        top: 0,
        position: 'absolute',
        border: `1px solid ${Colours.primary}`,
        right: 0,
    },
});
function getSliderElements(lineEl) {
    const points = lineEl.querySelectorAll('[class*="point"]');
    const rangeLineEl = lineEl.querySelector('[class*="range"]');
    return { points, lineEl, rangeLineEl };
}
function setPosition(property, rangeLine, sliderPointEl, value) {
    rangeLine.style[property] = `${value}px`;
    sliderPointEl.style[property] = `${value}px`;
}
function setValue(line, defaultValue) {
    const { points, lineEl, rangeLineEl } = getSliderElements(line.current);
    const defaultPoint1 = (lineEl.offsetWidth - points[0].offsetWidth) * defaultValue[0];
    setPosition('left', rangeLineEl, points[0], defaultPoint1);
    const defaultPoint2 = (lineEl.offsetWidth - points[1].offsetWidth) * (1 - defaultValue[1]);
    setPosition('right', rangeLineEl, points[1], defaultPoint2);
}
function getValue(line) {
    const { points, lineEl } = getSliderElements(line);
    const lineWidth = lineEl.offsetWidth - points[0].clientWidth;
    return [
        calculateValueInPercents(points[0].offsetLeft, lineWidth),
        calculateValueInPercents(points[1].offsetLeft, lineWidth),
    ];
}
function round(v) {
    return Math.round(v * 100) / 100;
}
function calculateValueInPercents(pointPosition, lineWidth) {
    const resPercent = !!pointPosition ? round(pointPosition / lineWidth) : 0;
    return resPercent;
}
function createChangeEvent(value) {
    return new CustomEvent('sliderchange', {
        detail: { value },
        bubbles: false,
        cancelable: false,
    });
}
function addDocumentEventListener(type, listener) {
    document.addEventListener(type, listener, true);
}
function removeDocumentEventListener(type, listener) {
    document.removeEventListener(type, listener, true);
}
function mouseDown(event) {
    const pointEl = event.target;
    const { points, lineEl, rangeLineEl } = getSliderElements(pointEl.parentElement);
    const startPosition = pointEl.getBoundingClientRect().left +
        pointEl.getBoundingClientRect().width / 2;
    const rememberedOffsetLeft = pointEl.offsetLeft;
    const lineWidth = lineEl.offsetWidth - pointEl.clientWidth;
    const left = points[0] === pointEl;
    const range = left
        ? [0, points[1].offsetLeft - pointEl.clientWidth]
        : [points[0].offsetLeft + points[0].offsetWidth, lineWidth];
    function move(x) {
        const newPosition = Math.min(Math.max(range[0], rememberedOffsetLeft + x - startPosition), range[1]);
        if (left) {
            setPosition('left', rangeLineEl, pointEl, newPosition);
        }
        else {
            setPosition('right', rangeLineEl, pointEl, lineWidth - newPosition);
        }
    }
    function mouseMove(e) {
        move(e.clientX);
    }
    function touchMove(e) {
        move(e.touches[0].clientX);
    }
    function mouseUp() {
        const value = getValue(lineEl);
        const event = createChangeEvent(value);
        // fire set value event
        lineEl.dispatchEvent(event);
        removeDocumentEventListener('mousemove', mouseMove);
        removeDocumentEventListener('touchmove', touchMove);
        removeDocumentEventListener('mouseup', mouseUp);
        removeDocumentEventListener('touchend', mouseUp);
    }
    addDocumentEventListener('mouseup', mouseUp);
    addDocumentEventListener('touchend', mouseUp);
    addDocumentEventListener('mousemove', mouseMove);
    addDocumentEventListener('touchmove', touchMove);
}
function RangeSlider({ defaultValue = [0, 1], onChange, }) {
    const styles = getStyles();
    const line = useRef(null);
    function keyDown(e) {
        const keyCode = e.keyCode;
        if (keyCode == KEY_UP || keyCode == KEY_DOWN) {
            const pointEl = e.target;
            const { points } = getSliderElements(pointEl.parentElement);
            const value = getValue(line.current);
            if (points[0] === pointEl) {
                if (keyCode == KEY_UP) {
                    value[0] = Math.min(value[0] + STEP, value[1] - STEP);
                }
                else if (keyCode == KEY_DOWN) {
                    value[0] = Math.max(value[0] - STEP, 0);
                }
            }
            else {
                if (keyCode == KEY_UP) {
                    value[1] = Math.min(value[1] + STEP, 1);
                }
                else if (keyCode == KEY_DOWN) {
                    value[1] = Math.max(value[1] - STEP, value[0] + STEP);
                }
            }
            setValue(line, value);
            const event = createChangeEvent(value);
            // fire set value event
            line.current.dispatchEvent(event);
        }
        return e;
    }
    function sliderChange(e) {
        if (onChange)
            onChange(e.detail.value);
    }
    function addPointEventListener(type, listener) {
        line.current.addEventListener(type, listener);
    }
    function removePointEventListener(type, listener) {
        line.current.removeEventListener(type, listener);
    }
    useLayoutEffect(() => {
        if (line) {
            if (defaultValue) {
                setValue(line, defaultValue);
            }
            addPointEventListener('keydown', keyDown);
            addPointEventListener('mousedown', mouseDown);
            addPointEventListener('touchstart', mouseDown);
            addPointEventListener('sliderchange', sliderChange);
        }
        return () => {
            removePointEventListener('keydown', keyDown);
            removePointEventListener('mousedown', mouseDown);
            removePointEventListener('touchstart', mouseDown);
            removePointEventListener('sliderchange', sliderChange);
        };
    }, []);
    return (React.createElement("div", { className: styles.root },
        React.createElement("div", { className: styles.line, ref: line },
            React.createElement("div", { className: styles.range }),
            React.createElement("div", { className: styles.point, tabIndex: 0 }),
            React.createElement("div", { className: styles.point, tabIndex: 0 }))));
}
export default RangeSlider;
//# sourceMappingURL=RangeSlider.js.map